import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress,
  LinearProgress, Chip, alpha, Button, Alert
} from '@mui/material';
import {
  Email as EmailIcon,
  Domain as DomainIcon,
  Check as EnabledIcon,
  Block as DisabledIcon,
  CloudUpload as UploadIcon,
  Analytics as StatsIcon,
  TrendingUp as TrendingIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const EmailExtractorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/email-extractor/emails/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sourceColors = {
    manual: '#10b981',
    csv: '#3b82f6',
    gmail_oauth: '#ea4335',
    gmail_imap: '#f59e0b',
    outlook: '#0078d4',
    yahoo: '#6001d2',
    pst: '#8b5cf6'
  };

  return (
    <Box>
      <Alert
        severity="info"
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/admin/settings?tab=emailExtractor')}
          >
            Open job settings
          </Button>
        }
      >
        Scheduled extract job, tags, and Promote-by-tag live under{' '}
        <strong>Administration → Settings → Email Extractor</strong>
        (last settings tile). Extracted Emails also shows Company/Contact tag columns and filters.
      </Alert>

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 32 }} />
            Email Extractor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Extract, manage, and organize email addresses from multiple sources
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/admin/settings?tab=emailExtractor')}
          >
            Job & Promote
          </Button>
          <Button
            variant="outlined"
            startIcon={<DomainIcon />}
            onClick={() => navigate('/email-extractor/domains')}
          >
            Domains
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => navigate('/email-extractor/import')}
          >
            Import Emails
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Global Stats */}
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">Total Emails</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stats?.global?.total?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', background: (theme) => `linear-gradient(135deg, ${alpha('#10b981', 0.1)} 0%, ${alpha('#10b981', 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EnabledIcon sx={{ color: '#10b981' }} />
                <Typography variant="subtitle2" color="text.secondary">Enabled</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981' }}>
                {stats?.global?.enabled?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', background: (theme) => `linear-gradient(135deg, ${alpha('#ef4444', 0.1)} 0%, ${alpha('#ef4444', 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DisabledIcon sx={{ color: '#ef4444' }} />
                <Typography variant="subtitle2" color="text.secondary">Disabled</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#ef4444' }}>
                {stats?.global?.disabled?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', background: (theme) => `linear-gradient(135deg, ${alpha('#8b5cf6', 0.1)} 0%, ${alpha('#8b5cf6', 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DomainIcon sx={{ color: '#8b5cf6' }} />
                <Typography variant="subtitle2" color="text.secondary">Unique Domains</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                {stats?.global?.uniqueDomains?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Source Breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StatsIcon />
                Emails by Source
              </Typography>
              {stats?.bySource?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {stats.bySource.map((item) => (
                    <Box key={item.source}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: sourceColors[item.source] || '#9e9e9e'
                            }}
                          />
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {item.source.replace(/_/g, ' ')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {parseInt(item.count).toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((parseInt(item.count) / stats.global.total) * 100, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: sourceColors[item.source] || '#9e9e9e',
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No emails extracted yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Domains */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon />
                Top Domains
              </Typography>
              {stats?.topDomains?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {stats.topDomains.map((item, index) => (
                    <Box key={item.domain} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          width: 24,
                          textAlign: 'center',
                          fontWeight: 600,
                          color: index < 3 ? 'primary.main' : 'text.secondary'
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.domain}
                        </Typography>
                      </Box>
                      <Chip
                        label={parseInt(item.count).toLocaleString()}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No domains yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Your Contribution */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Your Contribution
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {stats?.user?.total_uploads || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Uploads</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {stats?.user?.new_emails || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">New Emails Added</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {stats?.user?.duplicates || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Duplicates</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {stats?.user?.total_uploads > 0 
                        ? Math.round((stats?.user?.new_emails / stats?.user?.total_uploads) * 100) 
                        : 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">New Email Rate</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/email-extractor/emails')}
                >
                  View All Emails
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/email-extractor/history')}
                >
                  Extraction History
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => navigate('/email-extractor/import')}
                >
                  Manual Entry
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/email-extractor/domains')}
                >
                  Manage Domains
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailExtractorDashboard;
